// Firebase init, key encoding, auth helpers, and household management.
// Core database paths: users/{uid}, households/{householdId}, invites/{code}
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, onValue, set, get as fbGet } from 'firebase/database';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { reportToSentry } from './sentry';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const db = getDatabase(app);
const auth = getAuth(app);

// Firebase key encoding (forbidden chars: . # $ / [ ])
function encodeKey(k) {
  return k.replace(/%/g,'%25').replace(/\./g,'%2E').replace(/#/g,'%23')
    .replace(/\$/g,'%24').replace(/\//g,'%2F').replace(/\[/g,'%5B').replace(/\]/g,'%5D');
}
function decodeKey(k) {
  return k.replace(/%2E/g,'.').replace(/%23/g,'#').replace(/%24/g,'$')
    .replace(/%2F/g,'/').replace(/%5B/g,'[').replace(/%5D/g,']').replace(/%25/g,'%');
}
function encodeKeys(o) {
  if (o == null) return o;
  if (Array.isArray(o)) return o.map(encodeKeys);
  if (typeof o !== 'object') return o;
  const r = {}; for (const [k,v] of Object.entries(o)) r[encodeKey(k)] = encodeKeys(v); return r;
}
function decodeKeys(o) {
  if (o == null) return o;
  if (Array.isArray(o)) return o.map(decodeKeys);
  if (typeof o !== 'object') return o;
  const r = {}; for (const [k,v] of Object.entries(o)) r[decodeKey(k)] = decodeKeys(v); return r;
}

// Core database operations
export function subscribe(path, callback) {
  return onValue(ref(db, path), (snap) => callback(decodeKeys(snap.val())));
}

export async function write(path, data) {
  await set(ref(db, path), encodeKeys(data));
}

export async function read(path) {
  const snap = await fbGet(ref(db, path));
  return decodeKeys(snap.val());
}

export async function joinWaitlist(email) {
  const clean = String(email || '').trim().toLowerCase();
  if (!clean) throw new Error('Empty email');
  await set(ref(db, 'waitlist/' + encodeKey(clean)), {
    email: clean,
    createdAt: new Date().toISOString(),
  });
}

// Auth
export async function login(email, password) {
  return signInWithEmailAndPassword(auth, email, password);
}
export async function logout() { return signOut(auth); }
export function onAuthChange(cb) { return onAuthStateChanged(auth, cb); }

// Household management

// Excludes I, O, 0, 1 to avoid visual confusion. Length 256 % 32 = 0 so
// modulo mapping from a Uint8 byte is uniformly distributed.
const INVITE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const INVITE_CODE_LENGTH = 10;
// Accept 6-10 chars so codes created before hardening (length 6) still work.
const INVITE_CODE_RE = /^BUD-[A-HJ-NP-Z2-9]{6,10}$/;

function generateCode() {
  const bytes = new Uint8Array(INVITE_CODE_LENGTH);
  crypto.getRandomValues(bytes);
  let code = 'BUD-';
  for (let i = 0; i < INVITE_CODE_LENGTH; i++) {
    code += INVITE_ALPHABET[bytes[i] % INVITE_ALPHABET.length];
  }
  return code;
}

export function isValidInviteCode(code) {
  return typeof code === 'string' && INVITE_CODE_RE.test(code);
}

// Simple in-memory cooldown to blunt brute-force attempts from this tab.
// Real rate limiting must live in Firebase rules (TODO); this is just
// defence-in-depth for the common "paste and hammer" mistake.
const JOIN_COOLDOWN_MS = 1000;
let lastJoinAttempt = 0;

export async function getUserProfile(uid) {
  return await read('users/' + uid);
}

export async function createHousehold(uid, email, householdName) {
  const householdId = uid.slice(0, 8) + '-' + Date.now().toString(36);
  const inviteCode = generateCode();

  await write('households/' + householdId, {
    name: householdName,
    createdBy: uid,
    inviteCode: inviteCode,
    members: { [uid]: { email: email, joinedAt: new Date().toISOString() } },
  });

  await write('invites/' + inviteCode, { householdId: householdId, createdBy: uid });
  await write('users/' + uid, { householdId: householdId, email: email, inviteCode: inviteCode });

  return { householdId, inviteCode };
}

export async function joinHousehold(uid, email, code) {
  const now = Date.now();
  if (now - lastJoinAttempt < JOIN_COOLDOWN_MS) {
    throw new Error('Too many attempts. Wait a moment and try again.');
  }
  lastJoinAttempt = now;

  const cleanCode = String(code || '').trim().toUpperCase();
  if (!isValidInviteCode(cleanCode)) {
    throw new Error('Invalid invite code format.');
  }

  const invite = await read('invites/' + cleanCode);
  if (!invite || !invite.householdId) throw new Error('Invalid invite code. Please check and try again.');

  const householdId = invite.householdId;

  await write('households/' + householdId + '/members/' + uid, {
    email: email,
    joinedAt: new Date().toISOString(),
  });

  const household = await read('households/' + householdId);

  await write('users/' + uid, { householdId: householdId, email: email, inviteCode: cleanCode });

  return { householdId, householdName: (household && household.name) || 'My Household' };
}

export async function getHouseholdInfo(householdId) {
  return await read('households/' + householdId);
}

export async function leaveHousehold(uid, householdId) {
  // Remove user from household members
  try {
    await write('households/' + householdId + '/members/' + uid, null);
  } catch(e) {
    console.error(e);
    reportToSentry(e, { op: 'leaveHousehold', householdId });
  }
  // Clear user profile
  await write('users/' + uid, null);
}

export { db, auth };
